import { IQuery } from '@serodigital/hooks';
import { QueryOperator } from '@serodigital/ies-api-client';
import * as React from 'react';
import { DateTime } from 'luxon';

export interface FilterOptions {
  project?: string[];
  healthcheck?: boolean;
  status?: boolean;
  trial?: string;
  configProfileVersion?: string;
  firmwareVersion?: string[];
  beeMode?: string;
}
export interface IlistFilterContext {
  queryOptions: Partial<IQuery>;
  setQueryOptions: (queryOptions: Partial<IQuery>) => void;
  filter: string;
  setFilter: (filter: string) => void;
  filterOption: FilterOptions;
  setFilterOption: (filterOption: FilterOptions) => void;
  searchDropdownFilters: FilterOptions;
  setDropdownFilters: (dropdownFilters: FilterOptions) => void;
  filterCount: number;
  setFilterCount: (filterCount: number) => void;
}
export const ListFilterContext = React.createContext<IlistFilterContext>(undefined);

interface queryParams {
  key: string;
  operator: QueryOperator;
  valueOne: string | DateTime; //Our query builder always needs a valueOne, even for nulls
  valueTwo?: string | DateTime;
}

export const defaultFilterOptions: FilterOptions = {
  project: [],
  firmwareVersion: [],
};

export const ListFilterProvider = ({ children }) => {
  const [queryOptions, setQueryOptions] = React.useState<Partial<IQuery>>();
  const [filter, setFilter] = React.useState<string>();
  const [filterOption, setFilterOption] = React.useState<FilterOptions>(defaultFilterOptions);
  const [searchDropdownFilters, setDropdownFilters] = React.useState<FilterOptions>(defaultFilterOptions);
  const [filterCount, setFilterCount] = React.useState(0);

  const newQueryOptions = React.useMemo(() => {
    const beeStatusTimeRule = 1;
    const timeStampWithBeeStatusRule = DateTime.now().toUTC().minus({ hours: beeStatusTimeRule });
    const where: queryParams[] = [];
    const or: queryParams[] = [];

    if (searchDropdownFilters.configProfileVersion) {
      where.push({
        key: 'configProfileVersion',
        operator: QueryOperator.Equals,
        valueOne: searchDropdownFilters.configProfileVersion,
      });
    }

    if (searchDropdownFilters.configProfileVersion === null) {
      where.push({
        key: 'configProfileVersion',
        operator: QueryOperator.IsNull,
        valueOne: 'null',
      });
    }

    if (searchDropdownFilters.trial) {
      where.push({
        key: 'trialId',
        operator: QueryOperator.Equals,
        valueOne: searchDropdownFilters.trial,
      });
    }

    if (searchDropdownFilters.trial === null) {
      where.push({
        key: 'trialId',
        operator: QueryOperator.IsNull,
        valueOne: 'null',
      });
    }

    if (searchDropdownFilters.beeMode) {
      where.push({
        key: 'beeMode',
        operator: QueryOperator.Equals,
        valueOne: searchDropdownFilters.beeMode.toLowerCase(),
      });
    }

    if (searchDropdownFilters.beeMode === null) {
      where.push({
        key: 'beeMode',
        operator: QueryOperator.IsNull,
        valueOne: 'null',
      });
    }

    if (searchDropdownFilters.project.length > 0) {
      where.push({
        key: 'project',
        operator: QueryOperator.In,
        valueOne: JSON.stringify(searchDropdownFilters.project),
      });
    }

    if (searchDropdownFilters.firmwareVersion.length > 0) {
      where.push({
        key: 'currentFirmwareVersion',
        operator: QueryOperator.In,
        valueOne: JSON.stringify(searchDropdownFilters.firmwareVersion),
      });
    }

    if (searchDropdownFilters.healthcheck !== undefined) {
      where.push({
        key: 'isHealthy',
        operator: QueryOperator.Equals,
        valueOne: `${searchDropdownFilters.healthcheck}`,
      });
    }

    if (searchDropdownFilters.status !== undefined) {
      where.push({
        key: 'stateLastUpdated',
        operator: searchDropdownFilters.status ? QueryOperator.GreaterThanOrEqual : QueryOperator.LessThan,
        valueOne: timeStampWithBeeStatusRule,
      });
    }

    if (filter && filter.length > 0) {
      or.push(
        {
          key: 'deviceId',
          operator: QueryOperator.Like,
          valueOne: filter,
        },
        { key: 'routerId', operator: QueryOperator.Like, valueOne: filter },
        {
          key: 'physicalAddress.addressLine1',
          operator: QueryOperator.Like,
          valueOne: filter,
        },
        { key: 'plot', operator: QueryOperator.Like, valueOne: filter },
      );
    }

    return {
      or: or.length ? or : undefined,
      where: where.length > 0 ? where : undefined,
    };
  }, [filter, searchDropdownFilters]);

  React.useEffect(() => {
    setQueryOptions(newQueryOptions);
  }, [newQueryOptions]);

  React.useEffect(() => {
    const filterBadgeCount = Object.values(searchDropdownFilters)
      .map((filter) => {
        if (Array.isArray(filter)) {
          return filter.length;
        } else {
          return filter !== undefined ? 1 : 0;
        }
      })
      .reduce((acc, count) => acc + count, 0);
    setFilterCount(filterBadgeCount);
  }, [searchDropdownFilters]);

  return (
    <ListFilterContext.Provider
      value={{
        queryOptions,
        setQueryOptions,
        filter,
        setFilter,
        filterOption,
        setFilterOption,
        searchDropdownFilters,
        setDropdownFilters,
        filterCount,
        setFilterCount,
      }}
    >
      {children}
    </ListFilterContext.Provider>
  );
};

export const useListFilter = () => React.useContext(ListFilterContext);
